import { DataTableViewOptions } from "@/components/data-table/ColumnVisibilityControl";
import { PaginationControls } from "@/components/data-table/PaginationControls";
import Alert from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { ApolloError } from "@apollo/client";
import {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
import { useState } from "react";
import { LoadingMembersTable } from "./LoadingMembersTable";
import { memberColumns } from "./MemberColumns";
import { MembersTableContent } from "./MembersTableContent";
import { MemberTable } from "./types";

type Props = {
  error: ApolloError | undefined;
  members: MemberTable[];
  loading: boolean;
};

export function MembersDataTable({ error, members, loading }: Props) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const table = useReactTable({
    data: members,
    columns: memberColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      sorting,
      columnFilters,
      columnVisibility
    }
  });

  const sortingFilteringOrVisibilityInUse =
    sorting.length > 0 ||
    columnFilters.length > 0 ||
    !table.getIsAllColumnsVisible();

  if (error) {
    return (
      <Alert
        title="An error happened"
        description="The overview of organization members could not be shown. Please try again or report the issue."
      />
    );
  }
  return (
    <div>
      <div className="flex justify-between items-center pb-2">
        <Input
          placeholder="Search for member..."
          value={(table.getColumn("name")?.getFilterValue() as string) ?? ""}
          onChange={(event) =>
            table.getColumn("name")?.setFilterValue(event.target.value)
          }
          disabled={loading}
          className="max-w-sm"
        />

        <div className="flex items-center gap-2">
          {sortingFilteringOrVisibilityInUse && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSorting([]);
                setColumnFilters([]);
                setColumnVisibility({});
              }}
            >
              Reset table
            </Button>
          )}
          <DataTableViewOptions table={table} />
        </div>
      </div>

      <div className="rounded border">
        <Table className="table-fixed">
          <TableHeader>
            {table.getHeaderGroups()?.map((headerGroup) => {
              return (
                <TableRow className="" key={headerGroup.id}>
                  {headerGroup.headers?.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableHeader>
          <TableBody>
            {loading ? (
              <LoadingMembersTable />
            ) : (
              <MembersTableContent
                table={table}
                isFiltered={sortingFilteringOrVisibilityInUse}
              />
            )}
          </TableBody>
        </Table>
      </div>
      <div className="my-4">
        <PaginationControls table={table} />
      </div>
    </div>
  );
}
